import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Spinner, Tabs, Tab, Form, Button, Table, Badge, Image, Modal as BootstrapModal, Alert } from 'react-bootstrap'; // Adicionado Badge, Image e BootstrapModal
import { Line, Bar } from 'react-chartjs-2'; // Adicionado Bar para possível gráfico
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement, // Adicionado para Bar chart
  ArcElement, // Adicionado para Pie/Doughnut chart
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import { useEntidadeContext } from '../EntidadeContext'; // Importar contexto da entidade
import logoImage from '../img/logo.png'; // Importa a imagem da logo
import { useAuthContext } from '../context/AuthContext'; // Importar para verificar assinatura
import { useAlertModal } from '../AlertModalContext'; // Para feedback
import { useUserContext } from '../UserContext'; // Importar o contexto do usuário
import DashboardFinanceiroTab from '../components/financeiro/DashboardFinanceiroTab'; // Importar o componente da aba
import RelatoriosFaturamentoTab from '../components/financeiro/RelatoriosFaturamentoTab'; // Importar o componente da aba
import RelatoriosPagamentosTab from '../components/financeiro/RelatoriosPagamentosTab'; // Importar o componente da aba
import RelatoriosGerenciaisTab from '../components/financeiro/RelatoriosGerenciaisTab'; // Importar o componente da aba
import LancamentoDespesasTab from '../components/financeiro/LancamentoDespesasTab'; // Importar o componente da aba
import ConveniosTab from '../components/financeiro/ConveniosTab'; // Importar a nova aba de convênios


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Financeiro = () => {
  const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig, dynamicAccessConfig } = useUserContext(); // Obter estados do contexto
  const [financeiroResumo, setFinanceiroResumo] = useState(null);
  const { currentEntidade, isLoadingEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal();
  const { authState } = useAuthContext(); // Obter o estado de autenticação
  const navigate = useNavigate(); // Para o botão de redirecionamento

  // activeMainTabKey é usado para controlar qual aba principal está ativa.
  // O valor inicial pode ser a primeira aba permitida ou um fallback.
  const [activeMainTabKey, setActiveMainTabKey] = useState('');

  // Obter abas permitidas para Financeiro
  const permittedTabs = useMemo(() => {
    return getPermittedInternalTabs('financeiro', currentUserRole);
    },
    [getPermittedInternalTabs, currentUserRole, isLoadingAccessConfig, dynamicAccessConfig]
  );

  // NOVO: Memo para ordenar as abas, colocando 'resumoGeral' primeiro se existir.
  const sortedPermittedTabs = useMemo(() => {
    const resumoTab = permittedTabs.find(tab => tab.key === 'resumoGeral');
    if (resumoTab) {
      // Filtra as outras abas e coloca a de resumo no início
      const otherTabs = permittedTabs.filter(tab => tab.key !== 'resumoGeral');
      return [resumoTab, ...otherTabs];
    }
    // Se a aba de resumo não estiver presente, retorna a lista original
    return permittedTabs;
  }, [permittedTabs]);

  // Define a aba ativa inicial para a primeira permitida, ou 'resumoGeral' como fallback
  // Use useEffect para garantir que permittedTabs esteja pronto
  useEffect(() => {
    if (sortedPermittedTabs.length > 0 && !isLoadingAccessConfig) { // Garante que o loading terminou
      // Se activeMainTabKey não estiver definida ou não for uma aba permitida, define para a primeira permitida.
      if (!activeMainTabKey || !sortedPermittedTabs.some(tab => tab.key === activeMainTabKey)) {
        setActiveMainTabKey(sortedPermittedTabs[0].key);
      }
    }
  }, [sortedPermittedTabs, activeMainTabKey, isLoadingAccessConfig]); // Adiciona isLoadingAccessConfig


  // Estados para o modal de confirmação de exclusão de despesa
  const [showConfirmDeleteDespesaModal, setShowConfirmDeleteDespesaModal] = useState(false);
  const [despesaParaExcluir, setDespesaParaExcluir] = useState(null);

  // --- NOVOS ESTADOS PARA GESTÃO DE CONVÊNIOS ---
  const [showRecebidoModal, setShowRecebidoModal] = useState(false);
  const [showGlosaModal, setShowGlosaModal] = useState(false);
  const [pagamentoParaProcessar, setPagamentoParaProcessar] = useState(null);
  const [isProcessingConvenio, setIsProcessingConvenio] = useState(false);
  const [recebidoFormData, setRecebidoFormData] = useState({ valor_recebido: '', data_recebimento: new Date().toISOString().split('T')[0] });
  const [glosaJustificativa, setGlosaJustificativa] = useState('');
  // Chave para forçar o refresh da lista de pagamentos de convênio após uma ação
  const [refreshPagamentosKey, setRefreshPagamentosKey] = useState(0);

  // Função para abrir o modal de confirmação de exclusão (será passada para LancamentoDespesasTab)
  const handleOpenDeleteModal = (despesa) => {
    setDespesaParaExcluir(despesa);
    setShowConfirmDeleteDespesaModal(true);
  };

  const handleDeleteDespesa = async () => {
    if (!despesaParaExcluir) return;
    try {
      await axios.delete(`/api/despesas-operacionais/${despesaParaExcluir.id}`);
      showAlertModal('Sucesso', 'Despesa excluída com sucesso!', 'success');
      // A lista será recarregada dentro de LancamentoDespesasTab após a exclusão
    } catch (error) {
      showAlertModal('Erro', `Falha ao excluir despesa: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setShowConfirmDeleteDespesaModal(false);
      setDespesaParaExcluir(null);
    }
  };

  // --- NOVOS HANDLERS PARA GESTÃO DE CONVÊNIOS ---
  const handleOpenRecebidoModal = (pagamento) => {
    setPagamentoParaProcessar(pagamento);
    setRecebidoFormData({
      valor_recebido: pagamento.valor_a_receber || '',
      data_recebimento: new Date().toISOString().split('T')[0]
    });
    setShowRecebidoModal(true);
  };

  const handleOpenGlosaModal = (pagamento) => {
    setPagamentoParaProcessar(pagamento);
    setGlosaJustificativa('');
    setShowGlosaModal(true);
  };

  const handleCloseConvenioModals = () => {
    setShowRecebidoModal(false);
    setShowGlosaModal(false);
    setPagamentoParaProcessar(null);
  };

  const handleConfirmarRecebimento = async () => {
    if (!pagamentoParaProcessar) return;
    setIsProcessingConvenio(true);
    try {
      await axios.post(`/api/pagamentos/${pagamentoParaProcessar.pagamento_id}/marcar-recebido-convenio`, recebidoFormData);
      showAlertModal('Sucesso', 'Conta marcada como recebida!', 'success');
      setRefreshPagamentosKey(prev => prev + 1); // Força o refresh da lista
      handleCloseConvenioModals();
    } catch (error) {
      showAlertModal('Erro', `Falha ao marcar como recebido: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setIsProcessingConvenio(false);
    }
  };

  const handleConfirmarGlosa = async () => {
    if (!pagamentoParaProcessar) return;
    setIsProcessingConvenio(true);
    try {
      await axios.post(`/api/pagamentos/${pagamentoParaProcessar.pagamento_id}/marcar-glosado-convenio`, { justificativa_glosa: glosaJustificativa });
      showAlertModal('Sucesso', 'Conta marcada como glosada!', 'success');
      setRefreshPagamentosKey(prev => prev + 1); // Força o refresh da lista
      handleCloseConvenioModals();
    } catch (error) {
      showAlertModal('Erro', `Falha ao marcar como glosa: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setIsProcessingConvenio(false);
    }
  };


  const formatCurrency = (value) => {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
  };

  // --- INÍCIO DA VALIDAÇÃO DE ACESSO AO CONTEÚDO ---
  const isAccessBlocked = useMemo(() => {
    const subscription = authState.subscription;

    // Se a assinatura existe, não é vitalícia e não está ativa/em teste
    if (subscription && !subscription.plan?.is_lifetime) {
      const expirationDate = new Date(subscription.current_period_ends_at + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (today > expirationDate) {
        const diffTime = today.getTime() - expirationDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Bloqueia o acesso se expirou há mais de 5 dias
        if (diffDays > 5) {
          return true;
        }
      }
    }
    // Se nenhuma condição de bloqueio for atendida, permite o acesso.
    return false;
  }, [authState.subscription]);

  const BlockedContent = () => (
    <Alert variant="danger" className="text-center m-4">
      <Alert.Heading>Acesso ao Financeiro Restrito</Alert.Heading>
      <p>Seu plano expirou há mais de 5 dias. Para visualizar os dados financeiros e gerenciar suas contas, por favor, regularize sua assinatura.</p>
      <Button variant="danger" onClick={() => navigate('/pricing')}>Ver Planos e Regularizar</Button>
    </Alert>
  );

  if (isLoadingEntidade) { // Apenas o loading da entidade é relevante aqui agora
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <div style={{ border: '1px solid gray', backgroundColor: '#f5f5f5' }}>
      <div className="header" style={{ minHeight: '80px' }}>
        <Row className="p-1 align-items-center" style={{ minHeight: 'inherit' }}>
          <Col xs={12} md={6} style={{ minHeight: '60px' }}>
            <div className="d-flex align-items-center justify-content-start h-100">
              <Image src={logoImage} alt="Logo Clínica" style={{ maxHeight: '60px', marginLeft: '12px' }} />
              <h3 style={{ marginLeft: '15px', marginBottom: '0' }}>Painel de Controle Financeiro</h3>
            </div>
          </Col>
          <Col xs={12} md={6} className="text-end">
            {/* Espaço para futuros botões de ação no cabeçalho, se necessário */}
          </Col>
        </Row>
      </div>
      <div className="body p-3">
        {/* Renderização condicional: mostra o bloqueio ou o conteúdo normal */}
        {isAccessBlocked ? (
          <BlockedContent />
        ) : (
          <>
        <Container fluid className="p-0"> {/* Container fluid para o conteúdo interno, removendo padding padrão se necessário */}
          {/* <h1 className="mt-4 mb-4">Painel de Controle Financeiro</h1>  Removido, pois o título está no header */}
          <Tabs
            id="dashboard-main-tabs"
            activeKey={activeMainTabKey}
            onSelect={(k) => setActiveMainTabKey(k)}
            className="mb-3 financeiro-main-tabs" // Adicionada a classe aqui
          >
            {/* Renderiza as abas permitidas dinamicamente */}
            {sortedPermittedTabs.map(tabInfo => { // Renomeado para tabInfo para evitar conflito com o componente Tab
              let tabContent = null;
              if (tabInfo.key === 'resumoGeral') {
                tabContent = (
                  <DashboardFinanceiroTab
                    isActive={activeMainTabKey === tabInfo.key} // Passa o estado de ativação
                    currentEntidade={currentEntidade} // Passa a entidade
                    formatCurrency={formatCurrency}
                  />
                );
              } else if (tabInfo.key === 'relatoriosFaturamento') {
                tabContent = (
                  <RelatoriosFaturamentoTab
                    currentEntidade={currentEntidade}
                    isActive={activeMainTabKey === tabInfo.key} // Passa o estado de ativação
                    formatCurrency={formatCurrency}
                  />
                );
              } else if (tabInfo.key === 'relatoriosPagamentos') {
                tabContent = (
                  <RelatoriosPagamentosTab
                    currentEntidade={currentEntidade}
                    isActive={activeMainTabKey === tabInfo.key} // Passa o estado de ativação
                    formatCurrency={formatCurrency}
                    onMarcarRecebidoClick={handleOpenRecebidoModal} // Nova prop
                    onMarcarGlosadoClick={handleOpenGlosaModal}   // Nova prop
                    refreshKey={refreshPagamentosKey}             // Nova prop
                  />
                );
              } else if (tabInfo.key === 'relatoriosGerenciais') {
                tabContent = (
                  <>
                    {/* Renderiza o componente RelatoriosGerenciaisTab que agora contém o Accordion */}
                    {/* Este componente já parece gerenciar seu próprio fetch */}
                    <RelatoriosGerenciaisTab
                      currentEntidade={currentEntidade}
                      formatCurrency={formatCurrency}
                    />
                  </>
                );
              } else if (tabInfo.key === 'lancamentoDespesas') {
                tabContent = (
                  <LancamentoDespesasTab
                    currentEntidade={currentEntidade}
                    isActive={activeMainTabKey === tabInfo.key} // Passa o estado de ativação
                    formatCurrency={formatCurrency}
                    onDeleteClick={handleOpenDeleteModal} // Passa a função para abrir o modal de exclusão
                  />
                );
              } else if (tabInfo.key === 'convenios') {
                tabContent = (
                  <ConveniosTab
                    currentEntidade={currentEntidade}
                    isActive={activeMainTabKey === tabInfo.key}
                    formatCurrency={formatCurrency}
                  />
                );
              }
              return (
                <Tab
                  key={tabInfo.key}
                  eventKey={tabInfo.key}
                  title={tabInfo.title}
                >
                  {tabContent}
                </Tab>
              );
            })}

      </Tabs>
        </Container>
          </>
        )}
      </div>

      {/* --- NOVOS MODAIS PARA GESTÃO DE CONVÊNIOS --- */}
      <BootstrapModal show={showRecebidoModal} onHide={handleCloseConvenioModals} centered>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Confirmar Recebimento do Convênio</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <p>Você está confirmando o recebimento para o atendimento do paciente <strong>{pagamentoParaProcessar?.paciente_nome}</strong> no valor de <strong>{formatCurrency(pagamentoParaProcessar?.valor_a_receber)}</strong>.</p>
          <Form>
            <Form.Group className="mb-3" controlId="valorRecebido">
              <Form.Label>Valor Efetivamente Recebido (R$)*</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={recebidoFormData.valor_recebido}
                onChange={(e) => setRecebidoFormData({ ...recebidoFormData, valor_recebido: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="dataRecebimento">
              <Form.Label>Data do Recebimento*</Form.Label>
              <Form.Control
                type="date"
                value={recebidoFormData.data_recebimento}
                onChange={(e) => setRecebidoFormData({ ...recebidoFormData, data_recebimento: e.target.value })}
                required
              />
            </Form.Group>
          </Form>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCloseConvenioModals} disabled={isProcessingConvenio}>Cancelar</Button>
          <Button variant="success" onClick={handleConfirmarRecebimento} disabled={isProcessingConvenio}>
            {isProcessingConvenio ? <Spinner as="span" size="sm" /> : 'Confirmar Recebimento'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <BootstrapModal show={showGlosaModal} onHide={handleCloseConvenioModals} centered>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Marcar como Glosa (Pagamento Negado)</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <p>Você está marcando como "Glosa" a cobrança para o paciente <strong>{pagamentoParaProcessar?.paciente_nome}</strong> no valor de <strong>{formatCurrency(pagamentoParaProcessar?.valor_a_receber)}</strong>.</p>
          <p>Esta ação indica que o convênio não efetuou o pagamento.</p>
          <Form>
            <Form.Group className="mb-3" controlId="justificativaGlosa">
              <Form.Label>Justificativa (Opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={glosaJustificativa}
                onChange={(e) => setGlosaJustificativa(e.target.value)}
                placeholder="Ex: Documentação incorreta, procedimento não coberto..."
              />
            </Form.Group>
          </Form>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleCloseConvenioModals} disabled={isProcessingConvenio}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmarGlosa} disabled={isProcessingConvenio}>
            {isProcessingConvenio ? <Spinner as="span" size="sm" /> : 'Confirmar Glosa'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Modal de Confirmação de Exclusão de Despesa */}
      <BootstrapModal show={showConfirmDeleteDespesaModal} onHide={() => setShowConfirmDeleteDespesaModal(false)} centered>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Confirmar Exclusão</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          Tem certeza que deseja excluir a despesa "{despesaParaExcluir?.descricao || despesaParaExcluir?.categoria}" no valor de {formatCurrency(despesaParaExcluir?.valor)}?
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmDeleteDespesaModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDeleteDespesa}>Excluir</Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

    </div>
  );
};

export default Financeiro;
